import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../UserContext'; // ✅ Import User Context

const ConflictResolution = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'conflictresolution'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful for logged-in users only
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="page-container conflict-resolution-page">
      <h1 className="page-title">Conflict Resolution</h1>

      {/* Include the image */}
      <img
        src="/images/wf-conflictresolution.webp"
        alt="Conflict Resolution"
        className="page-image"
      />

      <p className="page-text">Welcome to the Conflict Resolution section of Wholeness Forward.</p>

      <p className="page-text">
        Conflict is a natural part of human relationships, and resolving it effectively is a vital skill for personal and professional success.
        Whether it arises from differences in values, goals, or expectations, conflict can be a source of growth and deeper understanding when approached constructively.
      </p>
      <p className="page-text">
        In this section, we’ll explore strategies to navigate disagreements, foster mutual respect, and achieve solutions that work for all parties involved.
      </p>

      <hr className="page-divider" />

      <h2 className="page-heading">Key Principles of Conflict Resolution</h2>
      <ul className="content-list">
        <li><strong>Active Listening:</strong> Listen to understand, not just to respond. Ensure all parties feel heard and valued.</li>
        <li><strong>Empathy:</strong> Try to see the situation from the other person’s perspective to understand their feelings and needs.</li>
        <li><strong>Focus on Interests, Not Positions:</strong> Identify the underlying needs and goals driving each person's stance.</li>
        <li><strong>Stay Calm and Respectful:</strong> Manage your emotions and communicate with respect, even when the conversation gets difficult.</li>
        <li><strong>Seek Win-Win Solutions:</strong> Aim for solutions that address the needs of all parties involved.</li>
      </ul>

      <hr className="page-divider" />

      <h2 className="page-heading">Curated Video Collection</h2>
      {loading ? (
        <p>Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  width="560"
                  height="315"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                <div className="video-actions">
                  {/* ✅ Helpful Button */}
                  <button
                    className="button"
                    onClick={() => handleMarkHelpful(video._id)}
                    disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                  >
                    {video.usersWhoClickedHelpful?.includes(user?.id)
                      ? `Helpful (${video.helpfulCount})`
                      : `Mark as Helpful (${video.helpfulCount || 0})`}
                  </button>
                </div>
                <hr />
              </div>
            ))
          ) : (
            <p className="no-videos-message">No videos found for this category.</p>
          )}
        </div>
      )}

      <hr className="page-divider" />

      <h3 className="subheading">Join the Conflict Resolution Forum</h3>
      <p className="page-text">
        Share your experiences, ask questions, and gain insights from others who are navigating and resolving conflicts in their lives.
        Whether you're managing workplace disagreements or personal challenges, this forum is a space for growth and learning.
      </p>
      <a href="/forum/Conflict_Resolution" className="btn">Join the Forum</a>
    </div>
  );
};

export default ConflictResolution;
